/**
 * Profit Dashboard — the reseller P&L view, built on lifecycle outcomes.
 *
 * Four sections:
 *   Pipeline    open lots being watched/bid: counts, open commitment, potential meat
 *   Inventory   won/listed lots: cost basis vs estimated resale value
 *   Realized    sold lots: invested, revenue (net of channel costs), profit, margin, win rate
 *   Calibration sold lots with AI/user estimates: how often the sale landed in range
 *
 * Pure compute + render; reads outcome data recorded via the lifecycle flow.
 *
 * @module portal/modules/profitDashboard
 */

import type { Lot, PortalSettings } from '../types';
import { escapeHtml, formatCurrencyDisplay, parseCurrencyValue } from './formatting';
import { calculateMeatOnBone, isLotClosed } from './lotOperations';
import { computeBudgetSummary } from './budget';
import { computeCostBasis, computeRealizedProfit, getLifecycleStatus } from './lifecycle';
import { getSellChannelById } from './sellChannels';

const round2 = (n: number): number => Math.round(n * 100) / 100;

export interface ProfitReport {
  pipeline: {
    watching: number;
    bidding: number;
    openCommitment: number;
    meatLowTotal: number | null;
    meatHighTotal: number | null;
  };
  inventory: {
    count: number;
    listedCount: number;
    costBasis: number;
    estimatedValueLow: number | null;
    estimatedValueHigh: number | null;
  };
  realized: {
    soldCount: number;
    invested: number;
    revenue: number;
    netRevenue: number;
    profit: number;
    marginPct: number | null;
    wonCount: number;
    lostCount: number;
    winRatePct: number | null;
  };
  calibration: {
    comparable: number;
    withinRange: number;
    aboveRange: number;
    belowRange: number;
    avgErrorPct: number | null;
  };
}

export function computeProfitReport(
  lots: Lot[],
  settings: PortalSettings | null | undefined
): ProfitReport {
  let watching = 0;
  let bidding = 0;
  let meatLowTotal: number | null = null;
  let meatHighTotal: number | null = null;

  let inventoryCount = 0;
  let listedCount = 0;
  let inventoryCost = 0;
  let estimatedValueLow: number | null = null;
  let estimatedValueHigh: number | null = null;

  let soldCount = 0;
  let invested = 0;
  let revenue = 0;
  let netRevenue = 0;
  let wonCount = 0;
  let lostCount = 0;

  let comparable = 0;
  let withinRange = 0;
  let aboveRange = 0;
  let belowRange = 0;
  let errorPctSum = 0;

  for (const lot of lots) {
    if (!lot || lot.deleted) continue;
    const status = getLifecycleStatus(lot);

    if (status === 'watching' || status === 'bidding') {
      // Closed-but-unresolved lots are neither pipeline nor inventory.
      if (isLotClosed(lot)) continue;
      if (status === 'bidding') bidding += 1;
      else watching += 1;
      const meat = calculateMeatOnBone(lot, settings);
      if (meat?.meatLow != null) meatLowTotal = (meatLowTotal ?? 0) + meat.meatLow;
      if (meat?.meatHigh != null) meatHighTotal = (meatHighTotal ?? 0) + meat.meatHigh;
      continue;
    }

    if (status === 'won' || status === 'listed') {
      wonCount += 1;
      inventoryCount += 1;
      if (status === 'listed') listedCount += 1;
      const cost = computeCostBasis(lot, settings);
      if (cost != null) inventoryCost += cost;
      const low = parseCurrencyValue(lot.lowSellValue ?? lot.lowSellValueRaw);
      const high = parseCurrencyValue(lot.highSellValue ?? lot.highSellValueRaw);
      if (low != null) estimatedValueLow = (estimatedValueLow ?? 0) + low;
      if (high != null) estimatedValueHigh = (estimatedValueHigh ?? 0) + high;
      continue;
    }

    if (status === 'sold') {
      wonCount += 1;
      soldCount += 1;
      const cost = computeCostBasis(lot, settings);
      const salePrice = lot.outcome?.salePrice;
      if (cost != null) invested += cost;
      if (typeof salePrice === 'number' && Number.isFinite(salePrice)) {
        revenue += salePrice;
        const channel = getSellChannelById(settings, lot.outcome?.saleChannel);
        const sellerCostsPct = channel ? channel.sellerCostsPct : 0;
        netRevenue += salePrice * (1 - sellerCostsPct / 100);

        // Calibration against the estimate range that informed the buy
        const low = parseCurrencyValue(lot.lowSellValue ?? lot.lowSellValueRaw);
        const high = parseCurrencyValue(lot.highSellValue ?? lot.highSellValueRaw);
        if (low != null && high != null && high > 0) {
          comparable += 1;
          if (salePrice < low) belowRange += 1;
          else if (salePrice > high) aboveRange += 1;
          else withinRange += 1;
          const midpoint = (low + high) / 2;
          if (midpoint > 0) {
            errorPctSum += ((salePrice - midpoint) / midpoint) * 100;
          }
        }
      }
      continue;
    }

    if (status === 'lost') {
      lostCount += 1;
    }
    // archived: ignored everywhere
  }

  const profit = round2(netRevenue - invested);
  const decided = wonCount + lostCount;

  return {
    pipeline: {
      watching,
      bidding,
      openCommitment: computeBudgetSummary(lots, settings).openCommitment,
      meatLowTotal: meatLowTotal != null ? round2(meatLowTotal) : null,
      meatHighTotal: meatHighTotal != null ? round2(meatHighTotal) : null,
    },
    inventory: {
      count: inventoryCount,
      listedCount,
      costBasis: round2(inventoryCost),
      estimatedValueLow: estimatedValueLow != null ? round2(estimatedValueLow) : null,
      estimatedValueHigh: estimatedValueHigh != null ? round2(estimatedValueHigh) : null,
    },
    realized: {
      soldCount,
      invested: round2(invested),
      revenue: round2(revenue),
      netRevenue: round2(netRevenue),
      profit,
      marginPct: invested > 0 ? round2((profit / invested) * 100) : null,
      wonCount,
      lostCount,
      winRatePct: decided > 0 ? round2((wonCount / decided) * 100) : null,
    },
    calibration: {
      comparable,
      withinRange,
      aboveRange,
      belowRange,
      avgErrorPct: comparable > 0 ? round2(errorPctSum / comparable) : null,
    },
  };
}

// ── Rendering ──────────────────────────────────────────────────────────

function money(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return formatCurrencyDisplay(value) ?? '—';
}

function signedMoney(value: number): string {
  const formatted = formatCurrencyDisplay(Math.abs(value)) ?? Math.abs(value).toFixed(2);
  return value < 0 ? `−${formatted}` : formatted;
}

function row(label: string, value: string, emphasis?: 'pos' | 'neg'): string {
  const cls =
    emphasis === 'pos'
      ? ' class="pl-row__value pl-row__value--pos"'
      : emphasis === 'neg'
        ? ' class="pl-row__value pl-row__value--neg"'
        : ' class="pl-row__value"';
  return `<div class="pl-row"><span class="pl-row__label">${escapeHtml(label)}</span><strong${cls}>${value}</strong></div>`;
}

function section(title: string, body: string, subtitle?: string): string {
  const sub = subtitle ? `<p class="pl-section__subtitle">${escapeHtml(subtitle)}</p>` : '';
  return `<section class="pl-section"><h3 class="pl-section__title">${escapeHtml(title)}</h3>${sub}${body}</section>`;
}

export function renderProfitDashboardHtml(report: ProfitReport): string {
  const { pipeline, inventory, realized, calibration } = report;

  const pipelineBody =
    row('Watching', String(pipeline.watching)) +
    row('Active bids', String(pipeline.bidding)) +
    row('Open commitment', money(pipeline.openCommitment)) +
    row(
      'Potential meat',
      pipeline.meatLowTotal == null && pipeline.meatHighTotal == null
        ? '—'
        : `${money(pipeline.meatLowTotal)} … ${money(pipeline.meatHighTotal)}`
    );

  const inventoryBody =
    row(
      'Items on hand',
      `${inventory.count}${inventory.listedCount ? ` (${inventory.listedCount} listed)` : ''}`
    ) +
    row('Cost basis', money(inventory.costBasis)) +
    row(
      'Estimated value',
      inventory.estimatedValueLow == null && inventory.estimatedValueHigh == null
        ? '—'
        : `${money(inventory.estimatedValueLow)} … ${money(inventory.estimatedValueHigh)}`
    );

  const profitEmphasis: 'pos' | 'neg' | undefined =
    realized.soldCount === 0 ? undefined : realized.profit >= 0 ? 'pos' : 'neg';
  const realizedBody =
    row('Sold', String(realized.soldCount)) +
    row('Invested', money(realized.invested)) +
    row('Revenue (gross)', money(realized.revenue)) +
    row('Revenue (net of selling costs)', money(realized.netRevenue)) +
    row(
      'Realized profit',
      realized.soldCount ? signedMoney(realized.profit) : '—',
      profitEmphasis
    ) +
    row('Margin', realized.marginPct != null ? `${realized.marginPct}%` : '—') +
    row(
      'Win rate',
      realized.winRatePct != null
        ? `${realized.winRatePct}% (${realized.wonCount}W / ${realized.lostCount}L)`
        : '—'
    );

  const calibrationBody = calibration.comparable
    ? row('Sales with estimates', String(calibration.comparable)) +
      row('Sold within estimate range', `${calibration.withinRange} of ${calibration.comparable}`) +
      row('Above range / below range', `${calibration.aboveRange} / ${calibration.belowRange}`) +
      row(
        'Avg vs estimate midpoint',
        calibration.avgErrorPct != null
          ? `${calibration.avgErrorPct > 0 ? '+' : ''}${calibration.avgErrorPct}%`
          : '—'
      )
    : '<p class="pl-empty">Record sales on lots with price estimates to see how accurate the estimates are.</p>';

  const empty =
    pipeline.watching +
      pipeline.bidding +
      inventory.count +
      realized.soldCount +
      realized.lostCount ===
    0
      ? '<p class="pl-empty">No activity yet. Track lots, mark them Won or Lost when auctions close, and record sales to build your P&amp;L.</p>'
      : '';

  return (
    '<div class="pl-dashboard">' +
    empty +
    section('Pipeline', pipelineBody, 'Open lots you are still deciding on') +
    section('Inventory', inventoryBody, 'Won, not yet sold') +
    section('Realized', realizedBody, 'Completed flips') +
    section('Estimate calibration', calibrationBody, 'Predicted range vs actual sale price') +
    (inventory.count + realized.soldCount > 0
      ? '<div class="pl-actions"><button type="button" id="export-inventory-csv" title="Download won/listed/sold lots with costs and realized profit">Download inventory CSV</button></div>'
      : '') +
    '</div>'
  );
}
